import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TextareaAutosize from "react-textarea-autosize";
import { ArrowUp } from "lucide-react";
import { LuChevronDown, LuInfo } from "react-icons/lu";
import { IconPhoto, IconMovie } from "@tabler/icons-react";
import {
  RiAddLargeLine,
  RiAttachmentLine,
  RiBookOpenLine,
  RiExpandLeftRightLine,
  RiContractLeftRightLine,
} from "@remixicon/react";
import {
  FileAttachmentHandler,
  FileAttachmentsPreview,
} from "@/components/hoc/fileAttachment";
import SelectedAdsPreview, {
  SelectedAdAttachment,
} from "@/components/hoc/fileAttachment/SelectedAdsPreview";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import {
  MediaLibraryDialog,
  LibraryAttachmentsPreview,
} from "@/components/hoc/mediaLibrary";
import { File as CustomFile } from "@/types";
import { LibraryMedia } from "@/types";
import AspectRatioDropdown, { AspectRatio } from "../tabs/AspectRatioDropdown";
import VariationsDropdown, { Variations } from "../tabs/VariationsDropdown";
import DangerZoneDropdown from "../tabs/DangerZoneDropdown";
import BrandLogoHandler from "../tabs/BrandLogoHandler";
import ModelDropdown, { Model } from "../tabs/ModelDropdown";
import VideoTypeDropdown from "../tabs/VideoTypeDropdown";
import MoodboardDropdown from "../tabs/MoodboardDropdown";
import UgcVideoEditingPromptDropdown from "../tabs/UgcVideoEditingPromptDropdown";
import { VideoType } from "../../types";
import type { Moodboard } from "../../types";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";

const ATTACHMENT_LIMIT = 5;

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "video/mp4",
  "video/webm",
] as const;

export type { SelectedAdAttachment };

export interface CreativeInputSectionProps {
  accountId?: string;
  // State values
  prompt: string;
  creativeType: "image" | "video";
  aspectRatio: AspectRatio;
  variations: Variations;
  avoidDangerZone: boolean;
  model?: Model;
  videoType?: VideoType;
  files: CustomFile[];
  libraryMedia: LibraryMedia[];
  uploadingFiles: UploadingFile[];
  isFileUploading: boolean;
  submitting?: boolean;
  popoverOpen: boolean;
  selectedAds?: SelectedAdAttachment[];
  moodboards?: Moodboard[];
  moodboardId?: string | null;
  moodboardsLoading?: boolean;
  ugcVideoEditingPromptId?: string | null;

  // Handlers
  onPromptChange: (value: string) => void;
  onCreativeTypeChange: (value: "image" | "video") => void;
  onAspectRatioChange: (value: AspectRatio) => void;
  onVariationsChange: (value: Variations) => void;
  onAvoidDangerZoneChange: (value: boolean) => void;
  onModelChange?: (value: Model) => void;
  onVideoTypeChange?: (value: VideoType) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLibraryAttachmentChange: (attachments: LibraryMedia[]) => void;
  onRemoveFile: (file: CustomFile) => void;
  onRemoveLibraryAttachment: (file: CustomFile) => void;
  onRemoveSelectedAd?: (adId: string) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => Promise<void>;
  onSubmit: () => void | Promise<void>;
  onPopoverOpenChange: (open: boolean) => void;
  onBrandLogoSelect?: (media: LibraryMedia) => void;
  onMoodboardChange?: (moodboardId: string | null) => void;
  onCreateMoodboard?: () => void;
  onEditMoodboard?: (moodboardId: string) => void;
  onUgcVideoEditingPromptChange?: (promptId: string | null) => void;
  onExpand?: () => void;

  // Configuration
  fileInputRef?: React.RefObject<HTMLInputElement>;
  className?: string;
  containerClassName?: string;
  showSubmitButton?: boolean;
  submitButtonIcon?: React.ReactNode;
  disabled?: boolean;
  creativeTypeDisabled?: boolean;
  disableAttachmentActions?: boolean;
  hideGenerationControls?: boolean;
  showCreativeTypeDropdown?: boolean;
  onFocusChange?: (focused: boolean) => void;
}

const CreativeInputSection: React.FC<CreativeInputSectionProps> = ({
  accountId,
  prompt,
  creativeType,
  aspectRatio,
  variations,
  avoidDangerZone,
  model: modelProp,
  videoType,
  files,
  libraryMedia,
  uploadingFiles,
  isFileUploading,
  submitting = false,
  popoverOpen,
  selectedAds = [],
  moodboards = [],
  moodboardId = null,
  moodboardsLoading = false,
  ugcVideoEditingPromptId = null,
  onPromptChange,
  onCreativeTypeChange,
  onAspectRatioChange,
  onVariationsChange,
  onAvoidDangerZoneChange,
  onModelChange,
  onVideoTypeChange,
  onFileChange,
  onLibraryAttachmentChange,
  onRemoveFile,
  onRemoveLibraryAttachment,
  onRemoveSelectedAd,
  onPaste,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onSubmit,
  onPopoverOpenChange,
  onBrandLogoSelect,
  onMoodboardChange,
  onCreateMoodboard,
  onEditMoodboard,
  onUgcVideoEditingPromptChange,
  onExpand,
  fileInputRef: externalFileInputRef,
  className = "",
  containerClassName = "",
  showSubmitButton = true,
  submitButtonIcon,
  disabled = false,
  creativeTypeDisabled = false,
  disableAttachmentActions = false,
  hideGenerationControls = false,
  showCreativeTypeDropdown = true,
  onFocusChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded && prompt.split("\n").length < 5) {
      setExpanded(false);
    }
  }, [prompt, expanded]);

  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = externalFileInputRef || internalFileInputRef;
  const model: Model = (() => {
    const v: unknown = modelProp;
    if (v === "gpt-image-1" || v === "gpt-image-1.5" || v === "nano-banana-pro") return v;
    return "gpt-image-1";
  })();

  const remainingCapacity =
    ATTACHMENT_LIMIT - (files.length + libraryMedia.length + selectedAds.length);
  const libraryAttachLimit = remainingCapacity;

  const handleBrandLogoSelect = (media: LibraryMedia) => {
    if (onBrandLogoSelect) {
      onBrandLogoSelect(media);
      return;
    }

    // Default behavior if no custom handler
    const capacity = remainingCapacity;
    if (capacity <= 0) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix={
            capacity === 0 ? "Attachment limit reached" : "Cannot attach more files"
          }
          subheadingPrefix={`You can only attach up to ${ATTACHMENT_LIMIT} files in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      onPopoverOpenChange(false);
      return;
    }

    // Add logo to library media attachments
    const filesById = new Map<string, LibraryMedia>();
    libraryMedia.forEach((file) => {
      if (!filesById.has(file.file_id)) {
        filesById.set(file.file_id, file);
      }
    });
    // Add the new logo if not already present
    if (!filesById.has(media.file_id)) {
      filesById.set(media.file_id, media);
    }
    onLibraryAttachmentChange(Array.from(filesById.values()));
    onPopoverOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!prompt.trim() || isFileUploading || disabled) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  const isSelectedMoodboardPending =
    creativeType === "image" && !!moodboardId && moodboardId.startsWith("optimistic-");
  const isSubmitDisabled =
    !prompt.trim() ||
    isFileUploading ||
    submitting ||
    disabled ||
    isSelectedMoodboardPending;

  const shouldShowUgcEditingPrompts =
    creativeType === "video" && (videoType === "ugc_mirage" || videoType === "ugc_veo");

  const handleContainerFocus = () => onFocusChange?.(true);
  const handleContainerBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      onFocusChange?.(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative border border-border-gray rounded-[16px] shadow-sm w-full bg-[#FAFAFA] p-4 pt-2 ${containerClassName}`}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={(e) => void onDrop?.(e)}
    >
      {(expanded || prompt.split("\n").length >= 5) && (
        <button
          type="button"
          onClick={() => {
            setExpanded((prev) => {
              if (!prev) onExpand?.();
              return !prev;
            });
          }}
          className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/5 transition-colors"
        >
          {expanded ? (
            <RiContractLeftRightLine
              size={16}
              className="text-brand-primary-400"
              style={{ transform: "rotate(-45deg)" }}
            />
          ) : (
            <RiExpandLeftRightLine
              size={16}
              className="text-brand-primary-400"
              style={{ transform: "rotate(-45deg)" }}
            />
          )}
        </button>
      )}
      {/* Attachments Preview */}
      <div
        className={`flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide pt-2 ${[...uploadingFiles, ...files].length > 0 || libraryMedia.length > 0 || selectedAds.length > 0 ? "mb-4" : ""}`}
      >
        {/* Selected Ads Preview */}
        {selectedAds.length > 0 && (
          <SelectedAdsPreview
            ads={selectedAds}
            onRemove={onRemoveSelectedAd}
            className="!overflow-visible py-0 flex-none"
          />
        )}
        {[...uploadingFiles, ...files].length > 0 && (
          <FileAttachmentsPreview
            files={files}
            onRemove={onRemoveFile}
            uploadingFiles={uploadingFiles}
            className="!overflow-visible py-0 flex-none"
          />
        )}
        {files.length > 0 && libraryMedia.length > 0 && (
          <Separator
            orientation="vertical"
            className="h-[60px] bg-[#D4D4D4] shrink-0 mx-1"
          />
        )}
        {libraryMedia.length > 0 && (
          <LibraryAttachmentsPreview
            attachments={libraryMedia.map((m) => ({
              id: m.file_id,
              public_url: m.file_url,
              file_name: m.file_name,
            }))}
            onRemove={onRemoveLibraryAttachment}
            className="!overflow-visible py-0 flex-none"
          />
        )}
      </div>

      {/* Textarea */}
      <TextareaAutosize
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        onPaste={onPaste}
        placeholder="Describe your creative..."
        minRows={expanded ? 4 : 1}
        maxRows={expanded ? 16 : 4}
        disabled={disabled}
        className={`w-full resize-none focus:outline-none bg-[#FAFAFA] text-brand-primary-800 font-normal text-[14px] leading-[18px] tracking-[0.02em] pr-8 scrollbar-hide ${className}`}
        style={{ transition: "height 0.25s ease" }}
        onKeyDown={handleKeyDown}
      />

      {/* Controls */}
      <div className="flex items-end gap-2 pt-2">
        {/* Left controls: plus dropdown and creative type */}
        <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
          {!disableAttachmentActions && (
            <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
              <PopoverTrigger asChild>
                <button className="" disabled={disabled}>
                  <RiAddLargeLine size={14} className="text-brand-primary-400 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-max p-1 bg-white shadow-none rounded-[12px] border-0 squircle squircle-radius-[12px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
              >
                {/* Attach File */}
                <FileAttachmentHandler
                  acceptableFileTypes={ALLOWED_FILE_TYPES.join(",")}
                  multiple={true}
                  isFileUploading={false}
                  fileInputRef={fileInputRef}
                  onFileChange={onFileChange}
                  disabled={isFileUploading || remainingCapacity <= 0 || disabled}
                  renderTrigger={({ onClick, disabled: triggerDisabled }) => (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (triggerDisabled) return;
                        onClick();
                      }}
                      className={`shrink-0 flex h-7 px-2 py-1 gap-2 items-center self-stretch cursor-pointer text-[12px] rounded-[6px] ${
                        triggerDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-hover-gray"
                      }`}
                    >
                      <RiAttachmentLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                      <span className="text-brand-primary-800 text-[12px]">
                        Attach File
                      </span>
                    </div>
                  )}
                />
                {/* Library */}
                <MediaLibraryDialog
                  onAttachmentChange={onLibraryAttachmentChange}
                  attachLimit={libraryAttachLimit}
                  limitMessage={`You can only attach up to ${ATTACHMENT_LIMIT} files in total.`}
                  attachments={libraryMedia}
                  onOpenChange={(open) => {
                    if (!open) {
                      onPopoverOpenChange(false);
                    }
                  }}
                  trigger={
                    <div
                      role="button"
                      tabIndex={0}
                      className="shrink-0 flex h-7 px-2 py-1 gap-2 items-center self-stretch cursor-pointer text-[12px] rounded-[6px] hover:bg-hover-gray"
                    >
                      <RiBookOpenLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                      <span className="text-brand-primary-800 text-[12px]">Library</span>
                    </div>
                  }
                />
                {/* Brand Logo */}
                <BrandLogoHandler
                  onLogoSelect={handleBrandLogoSelect}
                  onOpenChange={(open) => {
                    if (!open) {
                      onPopoverOpenChange(false);
                    }
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
          {!hideGenerationControls && (
            <>
              {showCreativeTypeDropdown && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={creativeTypeDisabled}>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 w-[148px] border-[1px]"
                    >
                      {creativeType === "image" ? (
                        <div className="flex items-center gap-2 text-brand-primary-800 font-normal text-[12px]">
                          <IconPhoto size={16} color="#FF4699" />
                          <span>Text to Image</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-brand-primary-800 font-normal text-[12px]">
                          <IconMovie size={16} strokeWidth={1.2} color="#018C62" />
                          <span>Text to Video</span>
                        </div>
                      )}
                      <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[148px] shadow-none rounded-[8px] border-border-gray border-[1px]"
                  >
                    <DropdownMenuItem
                      className="flex items-center gap-2 cursor-pointer"
                      textValue="image"
                      onClick={() => onCreativeTypeChange("image")}
                    >
                      <div className="flex items-center gap-2 text-brand-primary-800 font-normal text-[12px]">
                        <IconPhoto size={16} color="#FF4699" />
                        <span>Text to Image</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="flex items-center gap-2 cursor-pointer"
                      textValue="video"
                      onClick={() => onCreativeTypeChange("video")}
                    >
                      <div className="flex items-center gap-2 text-brand-primary-800 font-normal text-[12px]">
                        <IconMovie size={16} strokeWidth={1.4} color="#018C62" />
                        <span>Text to Video</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Model Dropdown - Only show for Text to Image */}
              {creativeType === "image" && onModelChange && (
                <ModelDropdown value={model} onChange={onModelChange} />
              )}
              {/* Video Type Dropdown - Only show for video */}
              {creativeType === "video" && videoType && onVideoTypeChange && (
                <VideoTypeDropdown value={videoType} onChange={onVideoTypeChange} />
              )}
              {/* UGC video editing prompts - only for UGC & UGC 1.5 */}
              {shouldShowUgcEditingPrompts &&
                accountId &&
                onUgcVideoEditingPromptChange && (
                  <UgcVideoEditingPromptDropdown
                    accountId={accountId}
                    value={ugcVideoEditingPromptId}
                    onChange={(promptId) => onUgcVideoEditingPromptChange(promptId)}
                    preload
                    disabled={disabled}
                  />
                )}
              {/* Aspect Ratio */}
              <AspectRatioDropdown
                value={creativeType === "video" ? "9:16" : aspectRatio}
                onChange={onAspectRatioChange}
                allowedOptions={creativeType === "video" ? ["9:16"] : undefined}
              />
              {/* Variations */}
              <VariationsDropdown value={variations} onChange={onVariationsChange} />
              {/* Danger Zone (Images only) */}
              {creativeType === "image" && (
                <DangerZoneDropdown
                  value={avoidDangerZone}
                  onChange={onAvoidDangerZoneChange}
                />
              )}
            </>
          )}
        </div>
        {/* Right-side Style (Moodboard) dropdown - Images only */}
        {!hideGenerationControls &&
          creativeType === "image" &&
          onMoodboardChange &&
          (moodboards.length > 0 || onCreateMoodboard) && (
            <MoodboardDropdown
              value={moodboardId}
              moodboards={moodboards}
              loading={moodboardsLoading}
              onChange={onMoodboardChange}
              onCreateMoodboard={onCreateMoodboard}
              onEditMoodboard={onEditMoodboard}
            />
          )}
        {/* Submit Button */}
        {showSubmitButton && (
          <Button
            variant={"ghost"}
            disabled={isSubmitDisabled}
            onClick={() => void onSubmit()}
            className={`w-8 h-8 rounded-[8px] p-0 ${
              isSubmitDisabled
                ? "bg-[#E8E8E2] text-[#617390] cursor-not-allowed"
                : "bg-[#FF4500] hover:bg-[#FF4500CC] text-white hover:text-white"
            }`}
          >
            {submitting ? (
              <LoadingSpinner className="w-4 h-4 text-white" />
            ) : submitButtonIcon ? (
              submitButtonIcon
            ) : (
              <ArrowUp size={18} className="shrink-0" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreativeInputSection;
